import {
  type ReleaseFactCheck,
  factCheckReleaseBlocker,
  factCheckReleaseState,
} from '@/lib/content-doctrine/article-factcheck';

/**
 * `claims-pending` — every editorial check passes, but the claims have not been
 * checked against sources for this version (never run, or run on an earlier
 * version). A failed claim check is `needs-review`.
 */
export type ArticleReviewState = 'ready' | 'needs-review' | 'review-limited' | 'claims-pending';
export type EditorialReviewState = 'ready' | 'needs-review' | 'review-limited';

export interface ArticleReleaseInput {
  webReadiness: { ready: boolean; isCurrent: boolean } | null | undefined;
  critiqueCurrent: boolean;
  critiqueStatus: string | null | undefined;
  unresolvedEditorialFindings: number;
  languageFindings: number;
  /** The stored claim check resolved against this revision's hash; null when none has run. */
  factCheck: ReleaseFactCheck | null;
}

function webChecksPass(input: ArticleReleaseInput): boolean {
  return Boolean(input.webReadiness?.isCurrent && input.webReadiness.ready);
}

function critiqueIsComplete(input: ArticleReleaseInput): boolean {
  return input.critiqueCurrent && input.critiqueStatus === 'reviewed';
}

/** The draft checks and the editorial review, without the claim check. */
export function editorialReviewState(input: ArticleReleaseInput): EditorialReviewState {
  if (
    !webChecksPass(input) ||
    input.unresolvedEditorialFindings > 0 ||
    input.languageFindings > 0
  ) {
    return 'needs-review';
  }
  return critiqueIsComplete(input) ? 'ready' : 'review-limited';
}

/**
 * The one review state for a new hand-off. The bulk badge, the Next-step filter
 * and the hand-off dialog all read this, so they cannot disagree.
 */
export function articleReviewState(input: ArticleReleaseInput): ArticleReviewState {
  const editorial = editorialReviewState(input);
  const claims = factCheckReleaseState(input.factCheck);
  if (editorial === 'needs-review' || claims === 'failed') return 'needs-review';
  if (editorial === 'review-limited') return 'review-limited';
  return claims === 'clear' ? 'ready' : 'claims-pending';
}

export function isReleaseReady(input: ArticleReleaseInput): boolean {
  return articleReviewState(input) === 'ready';
}

/** Why the editorial half is open, ignoring the claim check; null when it is clear. */
export function editorialDisabledReason(input: ArticleReleaseInput): string | null {
  if (!input.webReadiness) return 'Run the draft checks before handing this off.';
  if (!input.webReadiness.isCurrent) return 'Check this article again before handing it off.';
  if (!input.webReadiness.ready) return 'Fix the failing draft checks before handing this off.';
  if (input.unresolvedEditorialFindings > 0) {
    return 'Resolve the editorial findings before handing this off.';
  }
  if (input.languageFindings > 0) return 'Resolve the language findings before handing this off.';
  if (!critiqueIsComplete(input)) return 'Refresh the editorial review before handing this off.';
  return null;
}

export function releaseDisabledReason(input: ArticleReleaseInput): string | null {
  return editorialDisabledReason(input) ?? factCheckReleaseBlocker(input.factCheck);
}

export interface ExternalHandoffGateInput {
  releaseReady: boolean;
  disabledReason: string | null;
  hasLivePublicationForThisRevision: boolean;
}

export interface ExternalHandoffGate {
  allowed: boolean;
  reason: string | null;
}

export function externalHandoffGate(input: ExternalHandoffGateInput): ExternalHandoffGate {
  if (input.releaseReady || input.hasLivePublicationForThisRevision) {
    return { allowed: true, reason: null };
  }
  return {
    allowed: false,
    reason: input.disabledReason ?? 'Resolve the current article checks and decisions first.',
  };
}
